import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Play, Pause, Volume2, VolumeX, Eye, Star, Sparkles, ArrowLeft, ArrowRight, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { isRTL, useLanguageStore } from "@/stores/languageStore";
import carCoverVideo from "@/assets/car-cover-video.mp4";

export const CarCoverVideoShowcase = () => {
  const { t, language } = useLanguageStore();
  const rtl = isRTL(language);
  const ArrowIcon = rtl ? ArrowLeft : ArrowRight;
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-background via-muted/30 to-background overflow-hidden">
      <div className="container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <Badge className="mb-4 px-4 py-2 bg-primary/10 text-primary border-primary/20">
            <Sparkles className="w-4 h-4 ml-2" />
            {t('carCover.badge') || 'מוצר הדגל שלנו'}
          </Badge>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
            כיסוי רכב חכם שנפתח בלחיצה – ושומר על הרכב שלך נקי ומוגן בכל מזג אוויר
          </h2>
          <div className="flex flex-wrap justify-center gap-4 mt-6 text-lg">
            <span className="flex items-center gap-2">☀️ מגן משמש קיצונית</span>
            <span className="flex items-center gap-2">🌧️ שומר מגשם ולחות</span>
            <span className="flex items-center gap-2">🐦 חוסך לכלוך מציפורים</span>
            <span className="flex items-center gap-2">⚡ נפתח תוך 5 שניות</span>
          </div>
          
          {/* Reviews Button */}
          <Link to="/product/כיסוי-רכב-אוטומטי-חשמלי#reviews" className="inline-block mt-6">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-gold/20 to-primary/20 border border-gold/40 hover:border-gold/60 transition-all shadow-lg backdrop-blur-sm"
            >
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-4 h-4 fill-gold text-gold" />
                ))}
              </div>
              <span className="font-medium text-foreground">4.9</span>
              <span className="text-muted-foreground">|</span>
              <MessageCircle className="w-4 h-4 text-primary" />
              <span className="font-medium text-foreground">מה לקוחות אומרים על המוצר</span>
            </motion.div>
          </Link>
        </motion.div>

        {/* Video Showcase */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="relative max-w-5xl mx-auto"
        >
          {/* Main Video Container */}
          <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-border/50 group">
            <video
              ref={videoRef}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              className="w-full aspect-video object-cover"
            >
              <source src={carCoverVideo} type="video/mp4" />
            </video>

            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent pointer-events-none" />

            {/* Video Controls */}
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={togglePlay}
                  className="p-3 rounded-full bg-background/90 backdrop-blur-sm border border-border/50 hover:bg-primary hover:text-primary-foreground transition-all shadow-lg"
                >
                  {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                </button>
                <button
                  onClick={toggleMute}
                  className="p-3 rounded-full bg-background/90 backdrop-blur-sm border border-border/50 hover:bg-primary hover:text-primary-foreground transition-all shadow-lg"
                >
                  {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
              </div>

              {/* Product Info Badge */}
              <div className="hidden sm:flex items-center gap-3 bg-background/90 backdrop-blur-sm rounded-full px-5 py-2.5 border border-border/50 shadow-lg">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-4 h-4 fill-primary text-primary" />
                  ))}
                </div>
                <span className="text-sm font-medium">{t('carCover.bestseller') || 'הכי נמכר'}</span>
              </div>
            </div>

            {/* Play Button Overlay (when paused) */}
            {!isPlaying && (
              <button
                onClick={togglePlay}
                className="absolute inset-0 flex items-center justify-center bg-background/30 backdrop-blur-sm transition-opacity"
              >
                <div className="p-6 rounded-full bg-primary text-primary-foreground shadow-2xl">
                  <Play className="w-12 h-12" />
                </div>
              </button>
            )}
          </div>

          {/* CTA Below Video */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link to="/product/כיסוי-רכב-אוטומטי-חשמלי">
              <Button size="lg" className="gradient-gold hover:opacity-90 text-white shadow-xl px-8 py-6 text-lg font-bold">
                🟧 הגן על הרכב שלי עכשיו
              </Button>
            </Link>
            <div className="flex items-center gap-2 text-muted-foreground">
              <span className="text-2xl font-bold text-primary">₪950</span>
              <span className="text-sm line-through">₪1,100</span>
              <Badge variant="destructive" className="font-bold">-14%</Badge>
            </div>
          </motion.div>

          {/* Value Proposition Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
            className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto"
          >
            {/* Why not wait */}
            <div className="bg-charcoal/80 backdrop-blur-sm rounded-2xl p-5 border border-gold/20">
              <div className="text-xl mb-3">🔥</div>
              <h4 className="font-bold text-white mb-2 text-sm">למה לא לחכות?</h4>
              <p className="text-white/70 text-xs leading-relaxed">
                השמש שוחקת צבע, מייבשת גומיות ופוגעת בריפוד – כל יום הנזק מצטבר.
              </p>
            </div>

            {/* Cost of not protecting */}
            <div className="bg-charcoal/80 backdrop-blur-sm rounded-2xl p-5 border border-gold/20">
              <div className="text-xl mb-3">💸</div>
              <h4 className="font-bold text-white mb-2 text-sm">כמה עולה לא להגן?</h4>
              <div className="space-y-1 text-xs">
                <p className="text-destructive/90">❌ צביעה: ₪4,000–₪8,000</p>
                <p className="text-destructive/90">❌ תיקון ריפוד: יקר ומיותר</p>
                <p className="text-primary font-bold">✅ הפתרון: ₪950 בלבד</p>
              </div>
            </div>

            {/* Why it's worth it */}
            <div className="bg-charcoal/80 backdrop-blur-sm rounded-2xl p-5 border border-gold/20">
              <div className="text-xl mb-3">⚡</div>
              <h4 className="font-bold text-white mb-2 text-sm">למה זה שווה?</h4>
              <div className="space-y-1 text-xs text-white/80">
                <p>✔ חוסך כיסוי ידני יומי</p>
                <p>✔ שומר ערך הרכב</p>
                <p>✔ מונע בלאי מהשמש</p>
                <p>✔ חוסך ניקיונות</p>
              </div>
            </div>

            {/* Peace of mind */}
            <div className="bg-gradient-to-br from-primary/20 to-gold/20 backdrop-blur-sm rounded-2xl p-5 border border-gold/30">
              <div className="text-xl mb-3">🛡</div>
              <h4 className="font-bold text-white mb-2 text-sm">קנייה בראש שקט</h4>
              <div className="space-y-1 text-xs text-white/90">
                <p>✔ 12 חודשי אחריות</p>
                <p>✔ תשלום מאובטח</p>
                <p>✔ שירות לקוחות זמין</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
